import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, LOGOS } from "../lib/api";
import { toast } from "sonner";
import { Lock, ShieldCheck, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

const ROLE_LABELS = { manager: "Manager", worker: "Team", review: "Review" };
const FIELD_STYLE = { backgroundColor: "#0A1F44", border: "1px solid #1E3A8A" };

function SetupError({ error }) {
  return (
    <div data-testid="setup-error" className="text-center py-6">
      <AlertTriangle size={32} className="mx-auto mb-3" style={{ color: "#F59E0B" }} />
      <p className="text-white font-heading font-semibold text-lg mb-1">Link not available</p>
      <p className="text-[#9CA3AF] text-sm">{error}</p>
    </div>
  );
}

function SetupSuccess({ role }) {
  return (
    <div data-testid="setup-success" className="text-center py-6">
      <CheckCircle2 size={36} className="mx-auto mb-3" style={{ color: "#10B981" }} />
      <p className="text-white font-heading font-semibold text-lg mb-1">Password set</p>
      <p className="text-[#9CA3AF] text-sm mb-6">
        Your {ROLE_LABELS[role] || ""} portal password is now active. Only you know it.
      </p>
      <a href="/" data-testid="setup-goto-login"
        className="inline-block rounded-lg px-6 py-3 font-semibold text-white" style={{ backgroundColor: "#F97316" }}>
        Go to Portal Login
      </a>
    </div>
  );
}

function validatePassword(password, confirm) {
  if (password.length < 8) return "Password must be at least 8 characters";
  if (password !== confirm) return "Passwords do not match";
  return null;
}

function SetupForm({ token, info, onDone }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const problem = validatePassword(password, confirm);
    if (problem) { toast.error(problem); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/setup-links/${token}`, { password });
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not set password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-5" style={{ backgroundColor: "rgba(249,115,22,0.15)" }}>
        <Lock size={22} style={{ color: "#F97316" }} />
      </div>
      <h1 className="font-heading text-white text-2xl font-bold">Set your password</h1>
      <p className="text-[#9CA3AF] text-sm mt-1 mb-6" data-testid="setup-context">
        {ROLE_LABELS[info.role] || info.role} access · <span className="text-white font-medium">{info.client_name}</span>
      </p>
      <form onSubmit={submit} className="space-y-4">
        <input data-testid="setup-password-input" type="password" autoFocus value={password}
          onChange={(e) => setPassword(e.target.value)} placeholder="New password (min 8 characters)"
          className="w-full rounded-lg px-4 py-3 text-white outline-none" style={FIELD_STYLE} />
        <input data-testid="setup-confirm-input" type="password" value={confirm}
          onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm password"
          className="w-full rounded-lg px-4 py-3 text-white outline-none" style={FIELD_STYLE} />
        <button data-testid="setup-submit-button" type="submit" disabled={busy}
          className="w-full rounded-lg px-4 py-3 font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-60"
          style={{ backgroundColor: "#F97316" }}>
          {busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={18} />}
          {busy ? "Saving..." : "Set Password"}
        </button>
      </form>
      <p className="text-[#6B7280] text-xs mt-5 leading-relaxed">
        This link works once and expires 7 days after it was issued. Your password is known only to you —
        360 Safety Systems cannot see or retrieve it.
      </p>
    </>
  );
}

export default function SetupPassword() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/setup-links/${token}`)
      .then(({ data }) => setInfo(data))
      .catch((e) => setError(e.response?.data?.detail || "This setup link is invalid."));
  }, [token]);

  const renderBody = () => {
    if (error) return <SetupError error={error} />;
    if (done) return <SetupSuccess role={info?.role} />;
    if (!info) return <div className="flex justify-center py-10"><Loader2 size={28} className="animate-spin" style={{ color: "#F97316" }} /></div>;
    return <SetupForm token={token} info={info} onDone={() => setDone(true)} />;
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-6" style={{ backgroundColor: "#0A1F44" }}>
      <div className="w-full max-w-md fade-up">
        <div className="flex justify-center mb-8">
          <img src={LOGOS.whiteWordmark} alt="360 Safety Systems" className="h-24 w-auto" />
        </div>
        <div className="rounded-2xl p-8 border" style={{ backgroundColor: "#112A57", borderColor: "#1E3A8A" }}>
          {renderBody()}
        </div>
      </div>
    </div>
  );
}
